import Typography from "antd/lib/typography";
import clsx from "clsx";
import { HTMLAttributes, ReactNode, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import useScroll from "src/hooks/useScroll";

import { ArrowLeft } from "@sol/uikit/core/icons/Arrows/ArrowLeft";
import { ArrowRight } from "@sol/uikit/core/icons/Arrows/ArrowRight";
import { Button } from "@sol/uikit/general/Button/Button";

import MutltistepFormNavigation from "./MutltistepFormNavigation";
import { useMultistepContext } from "./useMultistep";

type Props = {
    className?: string;
    children: ReactNode;
    actions?: ReactNode;
    isSubmitting?: boolean;
    showPrevButton?: boolean;
    showNextButton?: boolean;
} & HTMLAttributes<HTMLDivElement>;

function MultistepFormLayout({ className, children, actions, isSubmitting, showPrevButton, showNextButton }: Props) {
    const [t] = useTranslation();

    const multistep = useMultistepContext();

    if (!multistep) {
        throw new Error("MultistepFormLayout should be use in the subtree of a MutltistepProvider");
    }

    const { next, prev, isFirstStep, isLastStep, jump, stepIndex } = multistep;

    const scroll = useScroll();

    useEffect(() => {
        if (scroll) {
            scroll.scrollTo({ top: 0, behavior: "smooth" });
        }
    }, [stepIndex]);

    const shouldShowPrevButton = useMemo(() => {
        if (showPrevButton === undefined) {
            return !isFirstStep;
        }

        return showPrevButton;
    }, [showPrevButton, stepIndex, isFirstStep]);

    const shouldShowNextButton = useMemo(() => {
        if (showNextButton === undefined) {
            return !isLastStep;
        }

        return showNextButton;
    }, [showNextButton, stepIndex, isLastStep]);

    return (
        <div className={clsx("grid w-full grid-flow-row auto-rows-max grid-cols-12 gap-ant-md", className)}>
            <div className="col-span-9">
                <div className="grid w-full grid-flow-row auto-rows-max grid-cols-12 gap-ant-md">
                    <section className="col-span-12 min-w-0">{children}</section>
                    <div className="col-span-12 flex min-w-0">
                        {shouldShowPrevButton && (
                            <Button
                                data-cy="multistep-prev"
                                icon={<ArrowLeft />}
                                iconPosition="start"
                                onClick={() => jump(prev)}
                                disabled={isSubmitting || prev === -1}
                            >
                                {t("@sol.forms.MultistepFormLayout.prev")}
                            </Button>
                        )}
                        <div style={{ flex: 1 }} />
                        {shouldShowNextButton && (
                            <Button
                                data-cy="multistep-next"
                                icon={<ArrowRight variant="antd" />}
                                iconPosition="end"
                                onClick={() => jump(next)}
                                disabled={isSubmitting || next === -1}
                                type="primary"
                            >
                                {t("@sol.forms.MultistepFormLayout.next")}
                            </Button>
                        )}
                    </div>
                </div>
            </div>
            <div className="col-span-3 self-start rounded-ant-lg bg-white p-ant-lg">
                <div className="flex w-full flex-col gap-ant-md">
                    <div className="flex w-full min-w-0 flex-col gap-ant-md">
                        <Typography.Text className="ant-typography text-ant-h3 font-ant-strong">
                            {t("@sol.forms.MultistepFormLayout.navigation.title")}
                        </Typography.Text>
                        <MutltistepFormNavigation multistep={multistep} isSubmitting={isSubmitting} />
                    </div>
                    {actions && <div className="col-span-12 min-w-0">{actions}</div>}
                </div>
            </div>
        </div>
    );
}

export default MultistepFormLayout;
